import {logger, TypeUtil} from 'yougile-util'
import {
  ChatMessageJSON,
  DataObjectId,
  LazyLoadMessagesGetResult
} from 'yougile-model'
import {Backend} from '@common/backend'
import {globals} from '@extensions/globals-base'
import {SearchResultItem, SearchResultItemType} from '@extensions/search'
import {DirectMessages} from './direct-messages'
import {Sync} from './sync'

export const getLazyMessagesBlock = async (data: {
  companyId: DataObjectId
  hubId: DataObjectId
  messageTimestamp: number
  before?: number
  after?: number
}): Promise<LazyLoadMessagesGetResult> => {
  const response = await Backend.postJSON<LazyLoadMessagesGetResult>(
    '/data/lazy-load/get-messages',
    {
      ...Backend.loadCred(),
      data
    }
  )

  return (
    response ?? {
      messages: [],
      totalMessages: 0
    }
  )
}

const reportsCache = new Map<DataObjectId, DataObjectId[]>()

export const getLazyReportTasks = async (
  reportId: DataObjectId
): Promise<DataObjectId[]> => {
  try {
    const reportData = await Backend.postJSON<{
      taskIds: DataObjectId[]
    }>(
      '/data/lazy-load/report/get',
      {
        companyId: globals.getCompanyId(),
        id: reportId,
        ...Backend.loadCred()
      },
      {
        reuseRequests: true,
        reuseMaxAge: 70_000,
        additionalKeyData: {
          updateTime: Sync.getCurrentSandbox().getHub(reportId)?.updateTime
        }
      }
    )

    reportsCache.set(reportId, reportData.taskIds)

    return reportData.taskIds
  } catch (e) {
    logger.log(e)
    if (reportsCache.has(reportId)) {
      return reportsCache.get(reportId) ?? []
    }

    return []
  }
}

export const SEARCH_LIMIT = 300

const SEARCH_REQUEST_CONFIG = {
  reuseRequests: true,
  reuseMaxAge: 20_000
} as const

export const getLazySearchResults = async (
  query: string,
  forceNoLimit?: boolean
): Promise<{
  result: SearchResultItem[]
  totalCount: number
}> => {
  const box = Sync.getCurrentSandbox()
  if (!box) {
    return {
      result: [],
      totalCount: 0
    }
  }
  const searchResults = await Backend.postJSON<{
    searchResults: {
      items: {
        type:
          | 'taskTitle'
          | 'taskDescription'
          | 'taskChecklist'
          | 'contactName'
          | 'groupChatTitle'
          | 'message'
          | 'id'
          | 'none'
        text: string
        id: string
        time: number
        messageData?: ChatMessageJSON
      }[]
      totalCount: number
    }
  }>(
    '/data/mobile-api/search',
    {
      companyId: globals.getCompanyId(),
      query: query,
      ...Backend.loadCred(),
      limit: forceNoLimit ? 1_000_000 : SEARCH_LIMIT,
      withSearchById: true,
      withTotalCount: true
    },
    SEARCH_REQUEST_CONFIG
  )
  const items = searchResults.searchResults.items
  const list = (
    await Promise.all(
      items.map(async item => {
        if (item.type === 'message') {
          const [hubId, messageTime] = item.id.split('|') as [
            DataObjectId,
            string
          ]
          const hubFromBox = box.getHub(hubId)
          const hub = hubFromBox || DirectMessages.getChatIfExists(hubId)
          if (!hub) {
            return null
          }

          return {
            entity: hubFromBox || DirectMessages.getDirectChatUser(hub) || hub,
            type: 'message' as SearchResultItemType,
            messageTime: +messageTime,
            scoreTime: item.time,
            score: 0,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: hubId,
            searchQuery: query,
            messageData: item.messageData
          }
        }
        if (item.type === 'id') {
          const hub = box.getHub(item.id)
          if (!hub) {
            return null
          }
          return {
            entity: hub,
            type: 'id' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 0,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        if (item.type === 'taskTitle') {
          const hub = box.getHub(item.id)
          if (!hub) {
            return null
          }
          return {
            entity: hub,
            type: 'task-title' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 0,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        if (item.type === 'taskDescription') {
          const hub = box.getHub(item.id)
          if (!hub) {
            return null
          }
          return {
            entity: hub,
            type: 'description' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 0,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        if (item.type === 'groupChatTitle') {
          const hub = box.getHub(item.id)
          if (!hub) {
            return null
          }
          return {
            entity: hub,
            type: 'group-chat-title' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 90,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        if (item.type === 'contactName') {
          const hub = DirectMessages.getChatIfExists(item.id as DataObjectId)
          if (!hub) {
            return null
          }
          const user = DirectMessages.getDirectChatUser(hub)
          if (!user) {
            return null
          }
          return {
            entity: user,
            type: 'contact-name' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 100,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        if (item.type === 'taskChecklist') {
          const hub = box.getHub(item.id)
          if (!hub) {
            return null
          }
          return {
            entity: hub,
            type: 'checklist-item' as SearchResultItemType,
            messageTime: 0,
            scoreTime: item.time,
            score: 0,
            group: 0,
            start: 0,
            end: Number.MAX_VALUE,
            chatId: item.id,
            searchQuery: query
          }
        }
        return null
      })
    )
  )
    .filter(TypeUtil.isTruthy)
    .sort((a, b) => b.score - a.score)
  return {
    result: list,
    totalCount: searchResults.searchResults.totalCount
  }
}
